'use client';

/**
 * VendorQuoteFormCard — full-bleed quote submission form (canvas).
 *
 * Mode5-native parallel paint of the legacy
 * `<VendorQuoteFormWidget />`. Authored by `cp`-then-rename per the
 * doctrine in `right_rail_and_canvas_ux_direction.md`: presentation
 * primitives that lived in `widgets/` for historical reasons get
 * lifted into the mode-native namespace verbatim — no legacy
 * infrastructure coupling (no closeTab/openTab, no retired stores,
 * no tab-system integration), so the cp is structurally clean.
 *
 * Same BFF contract: reads `vendor_portal_form_context`, writes via
 * the form's declared submit_strategy. Same Zustand stores
 * (`useGraphSelectionStore`, `useDevUserStore`, etc.). The legacy
 * widget keeps running in parallel for non-mode5 surfaces during
 * cohabitation; both retire when mode5 graduates.
 *
 * @see klyde_edge/ui_config/composition/cards/seller.py
 *      content_key="vendor_quote_form"
 *
 * @layer presentation
 * @arch_component_type canvas_card
 * @arch_domain vendor_portal
 */

import { Fragment, useState, useEffect, useCallback } from 'react';
import { CheckCircle, Loader2, AlertCircle, FileText, Clock, DollarSign, Upload, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';

import { api } from '@/lib/api/client';
import type { ExtractionMetadata, VPFormContextResponse, VPBadgeConfig } from '@/lib/api/types';
import { useExtractionStore } from '@/lib/stores/extraction-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { toast } from 'sonner';

// ============================================================
// Line Item Row
// ============================================================

interface LineFormData {
  line_cdk: string | null;
  product_sid: string | null;      // VP_* for virtual products
  product_name: string;
  product_sku: string;
  qty: number;
  unit_price: string;
  lead_time_days: string;
  notes: string;
  is_virtual: boolean;
  badge?: VPBadgeConfig | null;
}

function LineItemRow({
  line,
  index,
  currency,
  onChange,
  onSelect,
  isSelected,
  labels,
}: {
  line: LineFormData;
  index: number;
  currency: string;
  onChange: (index: number, field: keyof LineFormData, value: string) => void;
  onSelect?: () => void;
  isSelected?: boolean;
  labels: Record<string, string>;
}) {
  return (
    <tr
      className={`border-b border-border hover:bg-accent/30 cursor-pointer ${line.badge ? 'border-l-2 border-l-amber-500' : ''} ${isSelected ? 'bg-accent/50 ring-1 ring-primary/20' : ''}`}
      onClick={onSelect}
    >
      <td className={cn(text.title, 'px-3 py-2 max-w-xs')}>
        <div className="truncate" title={line.product_name}>
          {line.product_name}
        </div>
        {line.badge && (
          <span className={`inline-flex items-center px-1.5 py-0.5 mt-0.5 rounded text-[9px] font-semibold ${
            line.badge.variant === 'amber' ? 'bg-amber-500/15 text-amber-600' :
            line.badge.variant === 'blue' ? 'bg-blue-500/15 text-blue-600' :
            line.badge.variant === 'green' ? 'bg-green-500/15 text-green-600' :
            'bg-muted text-muted-foreground'
          }`}>
            {line.badge.label}
          </span>
        )}
      </td>
      <td className={cn(text.muted, 'px-3 py-2 font-mono max-w-48 truncate')}>
        {line.product_sku || line.product_sid || '-'}
      </td>
      <td className={cn(text.body, 'px-3 py-2 text-right tabular-nums')} onClick={(e) => e.stopPropagation()}>
        {line.is_virtual ? (
          <Input
            type="number"
            min={1}
            value={line.qty}
            onChange={(e) => onChange(index, 'qty', e.target.value)}
            className="h-8 w-16 text-right tabular-nums"
          />
        ) : (
          line.qty
        )}
      </td>
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          <span className={text.muted}>{currency}</span>
          <Input
            type="number"
            min={0}
            step={0.01}
            placeholder="0.00"
            value={line.unit_price}
            onChange={(e) => onChange(index, 'unit_price', e.target.value)}
            className="h-8 w-28 text-right tabular-nums"
          />
        </div>
      </td>
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <Input
          type="number"
          min={0}
          placeholder="0"
          value={line.lead_time_days}
          onChange={(e) => onChange(index, 'lead_time_days', e.target.value)}
          className="h-8 w-20 text-right tabular-nums"
        />
      </td>
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <Input
          type="text"
          placeholder={labels.notes_placeholder || 'e.g. MOQ, special terms'}
          value={line.notes}
          onChange={(e) => onChange(index, 'notes', e.target.value)}
          className="h-8 w-48"
        />
      </td>
    </tr>
  );
}

// ============================================================
// Main Widget
// ============================================================

interface VendorQuoteFormCardProps {
  entityId?: string;
  form_id?: string;
  form_strategy?: string;
}

export function VendorQuoteFormCard({
  entityId,
  form_id,
  form_strategy,
}: VendorQuoteFormCardProps = {}) {
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const currentUser = useDevUserStore((s) => s.selectedUser);

  const rfqSid = entityId ?? selectedNode?.id ?? null;
  const formContextStrategy = useCapabilityStore((s) => s.strategyName('vendor_portal', 'form_context'));
  const uploadStrategy = useCapabilityStore((s) => s.strategyName('vendor_portal', 'upload'));
  const strategy = form_strategy ?? formContextStrategy ?? 'vendor_portal_form_context';
  const canUploadQuote = uploadStrategy !== null;

  // Language — shared store (toggled from app shell, read here for BFF call)
  const lang = useLanguageStore((s) => s.lang) ?? 'en';

  // Form context from BFF
  const [formData, setFormData] = useState<VPFormContextResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable state
  const [lines, setLines] = useState<LineFormData[]>([]);
  const [validityDays, setValidityDays] = useState('30');
  const [quoteNotes, setQuoteNotes] = useState('');

  // Upload state
  const [stagedFile, setStagedFile] = useState<{ name: string; base64: string; sizeKB: number } | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState<ExtractionMetadata | null>(null);

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string; response_sid?: string } | null>(null);

  // Fetch form context
  useEffect(() => {
    if (!rfqSid) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setSubmitResult(null);

    api
      .getStrategyData<VPFormContextResponse>(strategy, {
        rfq_sid: rfqSid,
        lang,
      })
      .then((res) => {
        if (cancelled) return;
        if (!res.success || !res.data?.form_schema) {
          setError(res.error || 'Failed to load form context');
          return;
        }
        setFormData(res.data);

        // Initialize editable lines from prefill
        setLines(
          res.data.prefill.lines.map((l) => ({
            line_cdk: l.line_cdk,
            product_sid: l.product_sid ?? null,
            product_name: l.product_name,
            product_sku: l.product_sku ?? '',
            qty: l.qty,
            unit_price: l.unit_price?.toString() ?? '',
            lead_time_days: l.lead_time_days?.toString() ?? '',
            notes: l.notes ?? '',
            is_virtual: l.is_virtual ?? false,
            badge: l.badge ?? null,
          }))
        );
        setValidityDays(res.data.prefill.validity_days?.toString() ?? '30');
        setQuoteNotes(res.data.prefill.notes ?? '');
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rfqSid, strategy, currentUser?.name, lang]);

  // Handle line field change
  const handleLineChange = useCallback(
    (index: number, field: keyof LineFormData, value: string) => {
      setLines((prev) => {
        const next = [...prev];
        if (field === 'qty') {
          next[index] = { ...next[index], qty: parseInt(value) || 1 };
        } else {
          next[index] = { ...next[index], [field]: value };
        }
        return next;
      });
    },
    []
  );

  // Handle file selection (drag-drop or browse)
  const handleFileSelect = useCallback((file: File) => {
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are supported');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      setStagedFile({ name: file.name, base64, sizeKB: Math.round(file.size / 1024) });
      setExtractionResult(null);
    };
    reader.readAsDataURL(file);
  }, []);

  // Extract prices from staged PDF
  const handleExtract = useCallback(async () => {
    if (!stagedFile || !rfqSid) return;

    // Warn if prices already entered
    const hasExistingPrices = lines.some((l) => l.unit_price && parseFloat(l.unit_price) > 0);
    if (hasExistingPrices) {
      const confirmed = window.confirm('This will replace any prices you have already entered. Continue?');
      if (!confirmed) return;
    }

    setIsExtracting(true);
    setError(null);
    setExtractionResult(null);

    try {
      const res = await api.getStrategyData<VPFormContextResponse & { extraction: ExtractionMetadata }>(
        uploadStrategy!,
        {
          rfq_sid: rfqSid,
          file_data: stagedFile.base64,
          file_name: stagedFile.name,
        }
      );

      if (!res.success || !res.data?.form_schema) {
        setError(res.error || 'Extraction failed');
        return;
      }

      // Update lines from extraction prefill
      setLines(
        res.data.prefill.lines.map((l) => ({
          line_cdk: l.line_cdk,
          product_sid: l.product_sid ?? null,
          product_name: l.product_name,
          product_sku: l.product_sku ?? '',
          qty: l.qty,
          unit_price: l.unit_price?.toString() ?? '',
          lead_time_days: l.lead_time_days?.toString() ?? '',
          notes: l.notes ?? '',
          is_virtual: l.is_virtual ?? false,
          badge: l.badge ?? null,
        }))
      );

      setExtractionResult(res.data.extraction);
      useExtractionStore.getState().setConversationSid(res.data.extraction.conversation_sid);
      toast.success('PDF Extracted', {
        description: `Matched ${res.data.extraction.matched_count} of ${res.data.extraction.matched_count + res.data.extraction.unmatched_count} lines`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Extraction failed');
    } finally {
      setIsExtracting(false);
    }
  }, [stagedFile, rfqSid, lines]);

  // Validate before submit
  const validate = (): string | null => {
    const missingPrices = lines.filter((l) => !l.unit_price || parseFloat(l.unit_price) <= 0);
    if (missingPrices.length > 0) {
      return `Please enter a unit price for all ${missingPrices.length} line(s)`;
    }
    return null;
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!rfqSid || !formData) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const submission = {
        lines: lines.map((l) => ({
          line_cdk: l.line_cdk,
          product_sid: l.is_virtual ? l.product_sid : undefined,
          product_name: l.is_virtual ? l.product_name : undefined,
          qty: l.is_virtual ? l.qty : undefined,
          unit_price: parseFloat(l.unit_price),
          lead_time_days: l.lead_time_days ? parseInt(l.lead_time_days) : null,
          notes: l.notes || null,
          is_virtual: l.is_virtual || undefined,
        })),
        validity_days: parseInt(validityDays) || 30,
        notes: quoteNotes || null,
      };

      const res = await api.getStrategyData(
        formData.form_schema.submit_strategy,
        {
          rfq_sid: rfqSid,
          submission,
        }
      );

      if (res.success) {
        const sid = res.data?.response_sid as string | undefined;
        setSubmitResult({ success: true, message: 'Quote submitted successfully', response_sid: sid });
        toast.success('Quote Submitted', {
          description: `${sid ?? 'Response'} created for ${rfqSid}`,
        });
      } else {
        setSubmitResult({ success: false, message: res.error || 'Submission failed' });
      }
    } catch (err) {
      setSubmitResult({
        success: false,
        message: err instanceof Error ? err.message : 'Network error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Compute line total
  const lineTotal = lines.reduce((sum, l) => {
    const price = parseFloat(l.unit_price) || 0;
    return sum + price * l.qty;
  }, 0);

  // ---- Render states ----

  if (!rfqSid) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <FileText className="w-16 h-16 mb-4 opacity-50" />
        <p className={text.heading}>Select an RFQ</p>
        <p className={text.muted}>Choose an RFQ from the explorer to submit your quote</p>
      </div>
    );
  }

  if (!rfqSid) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <AlertCircle className="w-12 h-12 mb-3 opacity-50" />
        <p className={text.muted}>Select an RFQ to view the quote form.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-8 h-8 mb-3 animate-spin" />
        <p className={text.muted}>Loading quote form...</p>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <AlertCircle className="w-12 h-12 mb-3 text-destructive opacity-70" />
        <p className={cn(text.body, 'text-destructive')}>{error}</p>
      </div>
    );
  }

  if (!formData) return null;

  const { form_schema, context, labels: serverLabels } = formData;
  // Server-controlled labels — fallback to English if not provided
  const L = {
    line_items: 'Line Items',
    product: 'Product',
    sku: 'SKU',
    qty: 'Qty',
    unit_price: 'Unit Price',
    lead_time: 'Lead Time (days)',
    notes: 'Notes',
    notes_placeholder: 'e.g. MOQ, special terms',
    total: 'Total',
    submit: 'Submit Quote',
    extract_prices: 'Extract Prices',
    extracting: 'Extracting...',
    upload_prompt: 'Drop a quote PDF here or browse',
    upload_hint: 'PDF only, max 10MB',
    quote_submitted: 'Quote Submitted',
    select_rfq: 'Select an RFQ',
    select_rfq_hint: 'Choose an RFQ from the explorer to submit your quote',
    loading: 'Loading quote form...',
    revision: 'Revision',
    due: 'Due',
    general_notes: 'General terms, shipping notes, etc.',
    items: 'items',
    quote_details: 'Quote Details',
    validity_days: 'Quote Valid For (days)',
    additional_notes: 'Additional Notes',
    cancel: 'Cancel',
    submitting: 'Submitting...',
    line_items_summary: 'line items',
    total_label: 'total',
    ...serverLabels,
  };

  // Post-submit success view
  if (submitResult?.success) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <CheckCircle className="w-16 h-16 mb-4 text-green-500" />
        <p className={text.heading}>{L.quote_submitted}</p>
        {submitResult.response_sid && (
          <p className={cn(text.muted, 'font-mono mt-1')}>{submitResult.response_sid}</p>
        )}
        <p className={cn(text.muted, 'mt-2')}>{context.reference} - {lines.length} line items</p>
        <p className={cn(text.muted, 'mt-1')}>
          Total: {context.currency} {lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </p>
      </div>
    );
  }

  return (
    <div style={{ fontSize: surfaceBase.canvas }} className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-panel shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h2 className={text.heading}>{form_schema.title}</h2>
            <p className={cn(text.muted, 'mt-0.5')}>
              {context.reference} &middot; {context.project_name ?? 'Project'}
              {context.is_revision && (
                <span className="ml-2 text-amber-500 font-medium">{L.revision}</span>
              )}
            </p>
          </div>
          <div className={cn(text.muted, 'flex items-center gap-3')}>
            {context.deadline && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {L.due} {new Date(context.deadline).toLocaleDateString()}
              </span>
            )}
            <span className="flex items-center gap-1">
              <DollarSign className="w-3.5 h-3.5" />
              {context.currency}
            </span>
          </div>
        </div>
        {form_schema.description && (
          <p className={cn(text.muted, 'mt-1')}>{form_schema.description}</p>
        )}
      </div>

      {/* PDF Upload Zone — only if upload strategy available */}
      {canUploadQuote && (
      <div className="px-4 py-3 border-b border-border shrink-0">
        {!stagedFile ? (
          <label
            className="flex flex-col items-center justify-center gap-1.5 py-4 border-2 border-dashed border-muted-foreground/30 rounded-lg cursor-pointer hover:border-muted-foreground/50 hover:bg-accent/30 transition-colors"
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const file = e.dataTransfer.files?.[0];
              if (file) handleFileSelect(file);
            }}
          >
            <Upload className="w-5 h-5 text-muted-foreground" />
            <span className={text.muted}>
              {L.upload_prompt}
            </span>
            <span className={text.muted}>{L.upload_hint}</span>
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileSelect(file);
                e.target.value = '';
              }}
            />
          </label>
        ) : (
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <p className={cn(text.title, 'truncate')}>{stagedFile.name}</p>
              <p className={text.muted}>{stagedFile.sizeKB} KB</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setStagedFile(null); setExtractionResult(null); }}
              disabled={isExtracting}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              onClick={handleExtract}
              disabled={isExtracting}
            >
              {isExtracting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  {L.extracting}
                </>
              ) : (
                L.extract_prices
              )}
            </Button>
          </div>
        )}
        {extractionResult && (
          <div className={`mt-2 px-3 py-1.5 rounded text-sm ${
            extractionResult.success
              ? 'bg-green-500/10 text-green-700 dark:text-green-400'
              : 'bg-destructive/10 text-destructive'
          }`}>
            {extractionResult.success
              ? `Extracted ${extractionResult.matched_count}/${extractionResult.matched_count + extractionResult.unmatched_count} lines — review prices below`
              : 'Extraction failed — enter prices manually'}
          </div>
        )}
      </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-destructive/10 border-b border-destructive/20 text-destructive text-sm shrink-0">
          {error}
        </div>
      )}

      {/* Submit result error */}
      {submitResult && !submitResult.success && (
        <div className="px-4 py-2 bg-destructive/10 border-b border-destructive/20 text-destructive text-sm shrink-0">
          {submitResult.message}
        </div>
      )}

      {/* Scrollable form body */}
      <div className="flex-1 overflow-auto">
        {/* Line Items Table */}
        <div className="px-4 pt-3">
          <h3 className={cn(text.title, 'mb-2')}>{L.line_items}</h3>
          <div className="rounded-md border border-border overflow-x-auto">
            <table style={{ fontSize: surfaceBase.dense }} className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className={cn(text.muted, 'px-3 py-2 text-left font-medium')}>{L.product}</th>
                  <th className={cn(text.muted, 'px-3 py-2 text-left font-medium')}>{L.sku}</th>
                  <th className={cn(text.muted, 'px-3 py-2 text-right font-medium')}>{L.qty}</th>
                  <th className={cn(text.muted, 'px-3 py-2 text-left font-medium')}>{L.unit_price}</th>
                  <th className={cn(text.muted, 'px-3 py-2 text-left font-medium')}>{L.lead_time}</th>
                  <th className={cn(text.muted, 'px-3 py-2 text-left font-medium')}>{L.notes}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => {
                  const prev = i > 0 ? lines[i - 1] : null;
                  const showSeparator = line.is_virtual && (!prev || !prev.is_virtual);
                  return (
                    <Fragment key={line.line_cdk ?? `virtual-${i}`}>
                      {showSeparator && (
                        <tr><td colSpan={6} className="border-t-2 border-amber-400/50 py-0" /></tr>
                      )}
                      <LineItemRow
                        line={line}
                        index={i}
                        currency={context.currency}
                        onChange={handleLineChange}
                        labels={L}
                        isSelected={
                          selectedNode?.id === line.product_sid ||
                          selectedNode?.id === line.line_cdk
                        }
                        onSelect={() => {
                          const sid = line.product_sid || line.line_cdk;
                          if (sid) {
                            selectNode({
                              id: sid,
                              label: line.product_name,
                              type: line.product_sid ? 'product' : 'lineitem',
                            });
                          }
                        }}
                      />
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-muted/30">
                  <td colSpan={3} className={cn(text.title, 'px-3 py-2 text-right')}>
                    {L.total} ({lines.length} {L.items})
                  </td>
                  <td className={cn(text.body, 'px-3 py-2 font-semibold tabular-nums')}>
                    {context.currency}{' '}
                    {lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Quote Details */}
        <div className="px-4 pt-4 pb-2">
          <h3 className={cn(text.title, 'mb-2')}>{L.quote_details}</h3>
          <div className="grid grid-cols-2 gap-4 max-w-xl">
            <div>
              <label className={text.muted}>
                {L.validity_days}
              </label>
              <Input
                type="number"
                min={1}
                max={365}
                value={validityDays}
                onChange={(e) => setValidityDays(e.target.value)}
                className="h-8 mt-1"
              />
            </div>
          </div>
          <div className="mt-3 max-w-xl">
            <label className={text.muted}>
              {L.additional_notes}
            </label>
            <Textarea
              placeholder={L.general_notes}
              value={quoteNotes}
              onChange={(e) => setQuoteNotes(e.target.value)}
              className="mt-1 min-h-[60px]"
            />
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="px-4 py-3 border-t border-border bg-panel flex items-center justify-between shrink-0">
        <div className={text.muted}>
          {lines.length} {L.line_items_summary} &middot; {context.currency}{' '}
          {lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })} {L.total_label}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSubmitResult(null)}
            disabled={isSubmitting}
          >
            {L.cancel}
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {L.submitting}
              </>
            ) : (
              L.submit
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
